// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { Check, GripVertical, Plus, Trash2 } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { usePatchDeal } from "@/hooks/useDeals";
import { useStatusTemplates } from "@/hooks/useStatusTemplates";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { ModalCloseButton, ModalOverlay } from "@/components/ui/dialog";
import { FormCalendar, FormCheckbox, FormCustomField, FormInput, FormSelect, FormTextarea } from "@/components/forms/FormControls";
import { DEAL_SNAPSHOT_HEALTH } from "@/constant/deals.constant";
import {
  SNAPSHOT_HEALTH_OPTIONS,
  SNAPSHOT_OWNER_OPTIONS,
  SNAPSHOT_STATUS_OPTIONS,
} from "@/constant/deals-form.constant";
import { snapshotSchema } from "@/schemas/deals";
import { createSnapshotStep, getSnapshotDefaults } from "@/utils/deals.utils";
import { SnapshotOwner } from "@/enum/common.enum";
import { useAuth } from "@/lib/auth";
import { orderPickerOptions } from "@/utils/status-templates.utils";

export default function SnapshotEditor({ snapshot, dealId, onClose, onSaved }) {
  const form = useForm({ resolver: zodResolver(snapshotSchema), defaultValues: getSnapshotDefaults(snapshot) });
  const { fields, append, remove, move } = useFieldArray({ control: form.control, name: "steps", keyName: "fieldKey" });
  const { mutate: patchDeal, isPending: saving } = usePatchDeal();
  const { context } = useAuth();
  const orgId = context?.organization?.id ?? "";
  const { data: templates } = useStatusTemplates(orgId);
  const pickerOptions = orderPickerOptions(templates, snapshot?.status_note_slot ?? null);
  const autoSteps = form.watch("auto_steps");
  const note = form.watch("note");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((item) => item.fieldKey === active.id);
    const newIndex = fields.findIndex((item) => item.fieldKey === over.id);
    if (oldIndex >= 0 && newIndex >= 0) move(oldIndex, newIndex);
    form.setValue("auto_steps", false);
  };

  const submit = (values) => {
    const payload = {
      deal_health: values.health || null,
      current_status_note: values.note,
      next_steps_auto: values.auto_steps,
    };
    if (!values.auto_steps) {
      payload.next_steps = values.steps.filter((step) => step.label).map(({ label, owner_role, due_date, status }) => ({
        label,
        owner_role: owner_role === SnapshotOwner.UNASSIGNED ? null : owner_role || null,
        due_date: due_date || null,
        status,
      }));
    }
    patchDeal({ dealId, payload }, { onSuccess: (res) => onSaved(res.data) });
  };

  return (
    <ModalOverlay className="overflow-y-auto" testId="deal-snapshot-editor">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} className="bg-linen w-full max-w-2xl border border-edge my-8 max-h-[92vh] overflow-y-auto" noValidate>
          <header className="flex items-center justify-between px-8 py-6 border-b border-edge sticky top-0 bg-linen z-10">
            <div>
              <div className="overline text-stone mb-1">Status update</div>
              <h3 className="font-display text-2xl tracking-tight">Update the client view</h3>
            </div>
            <ModalCloseButton onClick={onClose} data-testid="snapshot-editor-close" />
          </header>

          <div className="px-8 py-8 space-y-8">
            <FormCustomField name="health" label="Health" description={'"Auto" lets stoop compute health from live signals. Pick a color to override.'}>
              {({ field }) => (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {SNAPSHOT_HEALTH_OPTIONS.map((health) => {
                    const config = DEAL_SNAPSHOT_HEALTH[health] || { label: "Auto", chipBg: "bg-white text-ink border-edge", dot: "bg-stone" };
                    const active = field.value === health;
                    return (
                      <Button variant="seamless" key={health || "auto"} type="button" onClick={() => field.onChange(health)} className={`border px-3 py-3 text-xs uppercase tracking-wide flex items-center justify-center gap-2 ${active ? "ring-2 ring-moss ring-offset-1 border-transparent" : "border-edge"} ${config.chipBg}`} data-testid={`snapshot-health-${health || "auto"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />{health ? config.label : "Auto"}
                      </Button>
                    );
                  })}
                </div>
              )}
            </FormCustomField>

            <div>
              {pickerOptions.length > 0 && (
                <label className="mb-4 block text-sm font-medium text-ink">
                  Insert template
                  <select
                    value=""
                    onChange={(event) => {
                      const option = pickerOptions.find((item) => item.id === event.target.value);
                      if (option) form.setValue("note", option.body, { shouldDirty: true });
                    }}
                    className="mt-1 h-10 w-full rounded-md border border-edge bg-white px-3 text-sm text-ink"
                    data-testid="snapshot-template-picker"
                  >
                    <option value="">Choose a template</option>
                    {pickerOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                </label>
              )}
              <FormTextarea
                name="note"
                label="Chapter update — a note to your client"
                description="Write it like a quick update: plain words, one thought, no jargon. Two to four sentences works best."
                rows={5}
                maxLength={600}
                placeholder="We're waiting on the appraisal — should hear back by Friday. Inspection resolved cleanly, so once it lands we're straight through to closing."
                data-testid="snapshot-note-input"
              />
              <div className="mt-1 text-right text-[10px] text-stone/70">{note.length}/600</div>
            </div>

            <fieldset className="space-y-4">
              <legend className="overline text-stone mb-3">Next steps (up to 3)</legend>
              <FormCheckbox name="auto_steps" label="Auto-derive from the checklist" data-testid="snapshot-auto-toggle" />
              {autoSteps ? (
                <p className="text-xs text-stone italic">Steps will populate from the next three non-complete checklist tasks in phase order.</p>
              ) : (
                <div className="space-y-3" data-testid="snapshot-steps-editor">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={fields.map((item) => item.fieldKey)} strategy={verticalListSortingStrategy}>
                      {fields.map((item, index) => <SortableStepRow key={item.fieldKey} id={item.fieldKey} index={index} onRemove={() => remove(index)} />)}
                    </SortableContext>
                  </DndContext>
                  {fields.length < 3 && (
                    <Button type="button" variant="outline" onClick={() => append(createSnapshotStep())} className="w-full border-dashed" data-testid="snapshot-add-step"><Plus /> Add a step</Button>
                  )}
                </div>
              )}
            </fieldset>
          </div>

          <footer className="flex items-center justify-end gap-3 px-8 py-5 border-t border-edge sticky bottom-0 bg-linen">
            <Button type="button" variant="outline" onClick={onClose} data-testid="snapshot-editor-cancel">Cancel</Button>
            <Button type="submit" variant="dashboardPrimary" loading={saving} data-testid="snapshot-editor-save"><Check /> Save status</Button>
          </footer>
        </form>
      </Form>
    </ModalOverlay>
  );
}

function SortableStepRow({ id, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }} className={`border border-edge bg-white p-3 ${isDragging ? "shadow-lg border-moss" : ""}`} data-testid={`snapshot-step-editor-${index}`}>
      <div className="mb-3 flex items-center justify-between">
        <Button variant="seamless" type="button" className="text-stone hover:text-moss p-1 cursor-grab" aria-label={`Drag to reorder step ${index + 1}`} data-testid={`snapshot-step-drag-${index}`} {...attributes} {...listeners}><GripVertical /></Button>
        <span className="text-xs text-stone">Step {index + 1}</span>
        <Button variant="seamless" type="button" onClick={onRemove} className="text-stone hover:text-rust" aria-label="Remove step" data-testid={`snapshot-step-remove-${index}`}><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
      <div className="space-y-3">
        <FormInput name={`steps.${index}.label`} label="Step label" placeholder="Plain English" />
        <div className="grid grid-cols-3 gap-2">
          <FormSelect name={`steps.${index}.owner_role`} label="Owner" options={SNAPSHOT_OWNER_OPTIONS} />
          <FormCalendar name={`steps.${index}.due_date`} label="Due date" />
          <FormSelect name={`steps.${index}.status`} label="Status" options={SNAPSHOT_STATUS_OPTIONS} />
        </div>
      </div>
    </div>
  );
}
